import type { IgdbSearchResult } from '@mcgames/contracts';
import { Button, Dialog, Spinner, TextInput } from '@mcgames/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../api/hooks.ts';
import { ErrorText } from './ErrorText.tsx';
import { useGameMutation } from './game-hooks.ts';
import styles from './Games.module.css';

export type IgdbPickerProps = {
  gameId: string;
  defaultQuery: string;
  onClose: () => void;
};

/** Search IGDB and import the chosen game into the open game (everything but the title). */
export function IgdbPicker({ gameId, defaultQuery, onClose }: IgdbPickerProps) {
  const { t } = useTranslation('admin');
  const [query, setQuery] = useState(defaultQuery);
  const [submitted, setSubmitted] = useState('');
  const results = useApiQuery<IgdbSearchResult[]>(
    `/api/admin/metadata/igdb/search?q=${encodeURIComponent(submitted)}`,
    { enabled: submitted !== '' },
  );
  const importGame = useGameMutation<{ igdbId: number }>(gameId, 'POST', '/igdb-import');

  function search(event: FormEvent) {
    event.preventDefault();
    setSubmitted(query.trim());
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('games.igdb.title')}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('actions.close', { ns: 'common' })}
        </Button>
      }
    >
      <p className={styles['hint']}>{t('games.igdb.hint')}</p>
      <form className={styles['row']} onSubmit={search}>
        <div className={styles['grow']}>
          <TextInput
            label={t('games.igdb.queryLabel')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={query.trim() === ''}>
          {t('games.igdb.search')}
        </Button>
      </form>
      {results.isFetching && <Spinner />}
      <ErrorText error={results.error ?? importGame.error} />
      {results.data?.length === 0 && <p className={styles['muted']}>{t('games.igdb.noResults')}</p>}
      {results.data !== undefined && results.data.length > 0 && (
        <ul className={styles['results']}>
          {results.data.map((result) => (
            <li key={result.id}>
              <button
                type="button"
                className={styles['result']}
                disabled={importGame.isPending}
                onClick={() => importGame.mutate({ igdbId: result.id }, { onSuccess: onClose })}
              >
                {result.coverThumbUrl !== null && (
                  <img className={styles['resultThumb']} src={result.coverThumbUrl} alt="" />
                )}
                {result.releaseYear !== null
                  ? `${result.name} (${result.releaseYear})`
                  : result.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {importGame.isPending && <p className={styles['muted']}>{t('games.igdb.importing')}</p>}
    </Dialog>
  );
}
